import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'

import { ConnectionIcon } from 'app/common/presentation'
import { useSignals } from 'app/signals/application'

import { Button, PageBanner, SegmentedControl } from 'components'

import styles from './CreateSignal.module.scss'
import PlatformCard from './PlatformCard'

const NewSignal = () => {
  const navigate = useNavigate()
  const { t } = useTranslation('signals')
  const { t: tConnections } = useTranslation('connections')
  const { fetchAvailableSignals, signalAvailability } = useSignals()
  const [filter, setFilter] = useState<'all' | 'ready' | 'needsData'>('all')

  useEffect(() => {
    void fetchAvailableSignals()
  }, [fetchAvailableSignals])

  const { available: signalConnections, unavailable: unavailableConnections } = signalAvailability

  const unavailableNames = unavailableConnections
    .map((name) => tConnections(`available.${name}.displayName`))
    .join(', ')
  const options = signalConnections.flatMap(({ audiences, conversions }) => [
    ...audiences,
    ...conversions,
  ])
  const readyCount = options.filter(({ valid }) => valid).length

  return (
    <div className={styles.createSignal}>
      <PageBanner subtitle={t('create.subtitle')} title={t('create.title')}>
        <SegmentedControl
          onChange={setFilter}
          options={[
            { label: t('create.filter.all', { count: options.length }), value: 'all' },
            { label: t('create.filter.ready', { count: readyCount }), value: 'ready' },
            {
              label: t('create.filter.needsData', { count: options.length - readyCount }),
              value: 'needsData',
            },
          ]}
          value={filter}
        />
      </PageBanner>

      <div className={styles.platforms}>
        {signalConnections.map((connection) => (
          <PlatformCard connection={connection} filter={filter} key={connection.name} />
        ))}

        {unavailableConnections.length > 0 && (
          <section className={styles.unavailableCard}>
            <div className={styles.unavailableContent}>
              <div className={styles.connectionIcons}>
                {unavailableConnections.map((name) => (
                  <span key={name}>
                    <ConnectionIcon connection={{ name }} style={{ height: 18, width: 18 }} />
                  </span>
                ))}
              </div>
              <div className={styles.unavailableText}>
                <strong>{t('create.unavailable.title', { platforms: unavailableNames })}</strong>
                <span>{t('create.unavailable.description')}</span>
              </div>
            </div>
            <Button onClick={() => void navigate('/connections/browse')} small variant="outlined">
              {t('create.action.addConnection')}
            </Button>
          </section>
        )}
      </div>
    </div>
  )
}

export default NewSignal
